import React from "react";
import styled from "styled-components/macro";
import { useMutation } from "relay-hooks";
import { useTranslation } from "react-i18next";
import { CreateSCard } from "../../../../queries/sources/createSCard";
import { EditSCard } from "../../../../queries/sources/editSCard";
import { createSCardMutation } from "../../../../queries/sources/__generated__/createSCardMutation.graphql";
import { editSCardMutation } from "../../../../queries/sources/__generated__/editSCardMutation.graphql";
import CardForm, { ADDCARDID } from "../../../main/card/CardForm";
import { CardValues } from "../../../../components/ReduxProvider";
import { useViewerDecks } from "../../../../queries/viewerQuery";
import SynchronizeCheckbox from "../../../../components/other/SynchronizeCheckbox";
import { bottomPadding } from "tools/device";
import { sourceEditQuery$data } from "../__generated__/sourceEditQuery.graphql";
import { PickerCard } from "../../../../components/picker/Picker";

const Wrap = styled.div`
  padding: 20px 20px ${bottomPadding + 30}px;
`;

type Props = {
  // Card being edited (undefined for new cards)
  sCard?: NonNullable<NonNullable<sourceEditQuery$data["node"]>["sCards"]>[0] | PickerCard;
  // Source the card belongs to
  source?: sourceEditQuery$data["node"];
  // Language for back side
  backLang?: string | null;
  // Callback after successful submit
  onSubmit?: VoidFunction;
};

const SCardForm: React.FC<Props> = ({ sCard, source, backLang, onSubmit }) => {
  const { t } = useTranslation();
  const [synchronize, setSynchronize] = React.useState(true);
  const { deck } = useViewerDecks();

  const [createSCard] = useMutation<createSCardMutation>(CreateSCard, {
    onCompleted: () => {
      setTimeout(() => {
        const el = document.getElementById(ADDCARDID);
        if (el !== null && el.scrollIntoView) el.scrollIntoView({ behavior: "smooth" });
      });
    }
  });

  const [editSCard] = useMutation<editSCardMutation>(EditSCard);

  const validate = (values: CardValues) => {
    if (source?.sCards?.some((c) => c.front === values.front && c.id !== sCard?.id)) {
      alert(t("That's already in the set."));
      return false;
    }
  };

  const handleSubmit = (values: CardValues) => {
    if (validate(values) === false) return;

    if (sCard?.id) {
      // Editing existing card
      editSCard({
        variables: {
          ...values,
          id: sCard.id,
          backLang,
          synchronize: sCard.isInMyDeck ? synchronize : false,
          deckId: deck ? deck.id : null
        }
      });
    } else {
      // Creating new card
      if (!source?.id) return;
      const variables = { ...values, sourceId: source.id, backLang };
      createSCard({ variables });
    }

    onSubmit?.();
  };

  if (!source || !source.lang) return null;

  const cardData: CardValues | undefined = sCard
    ? {
        front: sCard.front,
        back:
          typeof sCard.back === "object" && sCard.back?.value !== undefined
            ? sCard.back.value ?? ""
            : String(sCard.back || ""),
        hint: sCard.hint,
        svg: sCard.svg?.url ? { flatId: sCard.svg.flatId, url: sCard.svg.url } : null,
        theory:
          typeof sCard.theory === "object" && sCard.theory?.theory !== undefined
            ? sCard.theory.theory
            : typeof sCard.theory === "string"
              ? sCard.theory
              : null,
        sourceId: source?.id
      }
    : undefined;

  return (
    <Wrap>
      {!!(sCard && sCard.isInMyDeck) && <SynchronizeCheckbox checked={synchronize} onChange={setSynchronize} />}
      <CardForm
        card={cardData}
        withTheory
        withoutSorter={!sCard}
        onSave={handleSubmit}
        fromLang={source.lang}
        toLang={backLang}
        disableGrammar
      />
    </Wrap>
  );
};

export default SCardForm;
